import React, { useState, useEffect } from 'react';
import { TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText, Button, CircularProgress } from '@mui/material';
import { useSalonsListQuery, useTeamsTeamsPartialUpdateMutation, useAuthProfileRetrieveQuery } from '../../../../services/GLOWClient';
import theme from '../../../../theme';
import MuiDialog from '../../../../SystemDesign/MuiDialog';
import GroupsIcon from '@mui/icons-material/Groups';

const UpdateTeam = ({ open, onClose, onSuccess, team }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        salon: ''
    });
    const [errors, setErrors] = useState({});
    const [dialogClosed, setDialogClosed] = useState(false);
    
    // Get current user profile to filter salons by owner
    const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();
    
    // Fetch salons owned by the current user
    const { data: salonsData, isLoading: salonsLoading } = useSalonsListQuery(
        userProfile ? { owner: userProfile.id } : { skip: true }
    );
    
    const [updateTeam, { isLoading: isUpdating }] = useTeamsTeamsPartialUpdateMutation();
    
    // Initialize form data when team prop changes
    useEffect(() => {
        if (team) {
            setFormData({
                name: team.name || '',
                description: team.description || '',
                salon: team.salon_id || team.salon || ''
            });
        }
    }, [team]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when field is edited
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };
    
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = 'Team name is required';
        // Remove salon validation since salon field is commented out
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async () => {
        
        if (!validateForm()) {
            return;
        }
        
        try {
            // Ensure we have a valid team ID
            if (!team || (!team.team_id && !team.id)) {
                console.error('Missing team ID for update');
                setErrors({ general: 'Impossible de mettre à jour le groupe: ID manquant' });
                return;
            }
            
            // Format the data according to the API requirements
            const updateData = {
                teamId: team.team_id || team.id, // Use team_id or id for identifying the team
                patchedTeamRequest: {
                    name: formData.name.trim(),
                    description: formData.description?.trim() || ''
                }
            };
            
            const result = await updateTeam(updateData).unwrap();
            
            // Call success callback and close dialog
            onSuccess && onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to update team:', error);
            
            // Handle API validation errors
            if (error.data && error.data.errors) {
                setErrors(error.data.errors);
            } else if (error.data) {
                // Handle non-field errors or other error formats
                const apiErrors = {};
                
                if (error.data.name) apiErrors.name = error.data.name.join(' ');
                if (error.data.salon) apiErrors.salon = error.data.salon.join(' ');
                if (error.data.description) apiErrors.description = error.data.description.join(' ');
                if (error.data.non_field_errors) {
                    // Show non-field errors in the name field for visibility
                    apiErrors.name = error.data.non_field_errors.join(' ');
                }
                
                setErrors(apiErrors);
            }
        }
    };
    
    // Reset form and close dialog when onClose is called
    useEffect(() => {
        if (open === false) {
            setDialogClosed(true);
            setErrors({});
        } else {
            setDialogClosed(false);
        }
    }, [open]);

    // Form content to be displayed in the dialog
    const formContent = (
        <>
            {errors.general && (
                <div style={{ 
                    color: 'red', 
                    marginBottom: '16px', 
                    padding: '8px', 
                    backgroundColor: '#ffebee',
                    borderRadius: '4px'
                }}>
                    {errors.general}
                </div>
            )}
            <TextField
                fullWidth
                label="Nom"
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="normal"
                error={!!errors.name}
                helperText={errors.name}
                disabled={isUpdating}
            />
            
            <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description}
                disabled={isUpdating}
            />
            
            {/* <FormControl 
                fullWidth 
                margin="normal"
                error={!!errors.salon}
                disabled={isUpdating || salonsLoading || profileLoading}
            >
                <InputLabel>Salon</InputLabel>
                <Select
                    name="salon"
                    value={formData.salon}
                    onChange={handleChange}
                    label="Salon"
                >
                    {salonsLoading ? (
                        <MenuItem disabled>Loading salons...</MenuItem>
                    ) : salonsData && salonsData.length > 0 ? (
                        salonsData.map(salon => (
                            <MenuItem key={salon.salon_id} value={salon.salon_id}>
                                {salon.name}
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem disabled>No salons available</MenuItem>
                    )}
                </Select>
                {errors.salon && <FormHelperText>{errors.salon}</FormHelperText>}
            </FormControl> */}
        </>
    );

    // Trigger button for the dialog - this is hidden as we're controlling the dialog externally
    const triggerButton = <Button style={{ display: 'none' }}>Open</Button>;

    return (
        <MuiDialog
            open={open}
            icon={<GroupsIcon />}
            triggerButton={triggerButton}
            title="Modifier le groupe"
            subtitle={`Modifier les informations du groupe ${team?.name || ''}`}
            content={formContent}
            existAction={true}
            actionTitle="Mettre à jour"
            actionFunction={handleSubmit}
            onClose={dialogClosed}
            onExternalClose={onClose}
            isLoading={isUpdating}
            actionType="button"
            maxWidth="sm"
            disableAction={isUpdating}
        />
    );
};

export default UpdateTeam;